/**
 * Month ↔ week calendar (the iOS Calendar pattern). The month shows her rotation weeks
 * outlined and the route on each day; folding keeps only the selected day's week.
 */
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { addDays, addMonths, type CalendarDate } from "@hitl/rotations-domain";
import { isRotationWeek, monthGrid, weekDays, type PlannerState } from "../state/plannerState";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

/** "Bloem Noord" → "BN", "Welkom run" → "WR", "Bloemfontein" → "BL": legible in a 50px calendar cell. */
export function routeInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return words.slice(0, 3).map((w) => w[0]!.toUpperCase()).join("");
}

export function monthTitle(on: CalendarDate): string {
  const [y, m] = on.split("-").map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}

/** "2–8 Nov 2026", or "30 Nov – 6 Dec 2026" across a month boundary: names the week the folded strip shows. */
export function weekTitle(on: CalendarDate): string {
  const days = weekDays(on);
  const [first, last] = [days[0]!, days[6]!];
  const short = (d: CalendarDate) => MONTHS[Number(d.slice(5, 7)) - 1]!.slice(0, 3);
  const year = last.slice(0, 4);
  return short(first) === short(last)
    ? `${Number(first.slice(8))}–${Number(last.slice(8))} ${short(last)} ${year}`
    : `${Number(first.slice(8))} ${short(first)} – ${Number(last.slice(8))} ${short(last)} ${year}`;
}

export function Calendar(props: {
  planner: PlannerState;
  today: CalendarDate;
  selected: CalendarDate;
  /** Any day in the month being shown. */
  month: CalendarDate;
  folded: boolean;
  attention: ReadonlySet<string>;
  onSelect: (on: CalendarDate) => void;
  onMonth: (on: CalendarDate) => void;
  onFold: (folded: boolean) => void;
}) {
  const { planner, today, selected, month, folded, attention } = props;
  const days = folded ? weekDays(selected) : monthGrid(month);
  const inMonth = (on: string) => on.slice(0, 7) === month.slice(0, 7);
  const routeName = (on: string) => planner.routes.find((r) => r.id === planner.schedule[on])?.name;

  return (
    <section className="cal" aria-label="Calendar">
      <div className="cal__head">
        <h2 className="cal__title">{folded ? weekTitle(selected) : monthTitle(month)}</h2>
        {/* Folded, the arrows step a week (the next rotation week is a tap away); open, a month. */}
        <div className="cal__nav">
          <button
            aria-label={folded ? "Previous week" : "Previous month"}
            onClick={() => (folded ? props.onSelect(addDays(selected, -7)) : props.onMonth(addMonths(month, -1)))}
          >
            <ChevronLeft aria-hidden size={22} />
          </button>
          <button
            aria-label={folded ? "Next week" : "Next month"}
            onClick={() => (folded ? props.onSelect(addDays(selected, 7)) : props.onMonth(addMonths(month, 1)))}
          >
            <ChevronRight aria-hidden size={22} />
          </button>
        </div>
      </div>
      <div className="cal__dow" aria-hidden>
        {WEEKDAYS.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="cal__grid" role="grid">
        {days.map((on) => {
          const route = routeName(on);
          const classes = [
            "cal__day",
            !folded && !inMonth(on) ? "is-out" : "",
            isRotationWeek(planner, on) ? "is-rotation" : "",
            on === selected ? "is-selected" : "",
            on === today ? "is-today" : "",
            on < today ? "is-past" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={on}
              role="gridcell"
              className={classes}
              aria-selected={on === selected}
              aria-label={`${Number(on.slice(8))} ${monthTitle(on as CalendarDate)}${route ? `, ${route}` : ""}${attention.has(on) ? ", needs attention" : ""}`}
              onClick={() => props.onSelect(on)}
            >
              <span className="cal__num">{Number(on.slice(8))}</span>
              {attention.has(on) && <span className="cal__dot" aria-hidden />}
              {route && (
                <span className="cal__route" title={route}>
                  {routeInitials(route)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button className="cal__fold" onClick={() => props.onFold(!folded)}>
        {folded ? <ChevronDown aria-hidden size={18} /> : <ChevronUp aria-hidden size={18} />}
        {folded ? "Show the month" : "Show this week only"}
      </button>
    </section>
  );
}
